import { Injectable, Injector, Optional } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { environment } from "../environments/environment";
import { Community, CommunityChannel, FSP_COMMUNITY_STRING_ID, canPostInChannel, userGroupNames } from "../models/community.model";
import { HelperService } from "./helper.service";
import { UserService } from "./user.service";

/**
 * Cross-platform cache of community configs. Fetches `GET {paeApiUrl}communities` on app
 * boot (or on first access) and hands out community configs to shared UI components that
 * need to switch behavior per community — editor choice, subject_required, filter bar,
 * branding, etc.
 *
 * Multi-community expansion — DENG-727 Phase 3.
 *
 * Usage:
 *   - Call `load()` from app bootstrap (or after login) to prime the cache. Idempotent —
 *     concurrent callers share one in-flight request.
 *   - Call `getCommunity(stringId)` synchronously anywhere. Returns the cached community
 *     if the cache is warm, otherwise undefined; callers can subscribe to `communities$`
 *     for reactive updates or await `load()` if they need to be sure.
 */
@Injectable({
  providedIn: "root",
})
export class CommunitiesService {
  private _communities$ = new BehaviorSubject<Community[] | null>(null);
  public communities$ = this._communities$.asObservable();
  public communitiesMap: Record<string, Community> = {};
  private _loadingPromise: Promise<Community[]> | null = null;
  /** Every community paeapi returned; what's published is this less what the viewer can't see. */
  private _all: Community[] | null = null;
  private _viewerIsEmployee = false;
  private _userService: UserService | null | undefined;
  private _watchingUser = false;

  /**
   * `UserService` comes from the injector when first needed, not as a constructor parameter:
   * it imports the core barrel, which re-exports this file, so naming it here left it
   * undefined while this class's metadata was built — and CoreModule's providers with it.
   */
  constructor(
    private http: HttpClient,
    private helperService: HelperService,
    @Optional() private injector?: Injector
  ) {}

  private _users(): UserService | null {
    if (this._userService === undefined) this._userService = this.injector ? this.injector.get(UserService, null) : null;
    if (this._userService && !this._watchingUser) {
      this._watchingUser = true;
      // `employee_only` communities come and go with the signed-in user: re-publish when that
      // changes between employee and not.
      this._userService.user$.subscribe(() => {
        const employee = this._isEmployee();
        if (employee === this._viewerIsEmployee) return;
        this._viewerIsEmployee = employee;
        if (this._all) this._publish(this._all);
      });
    }
    return this._userService || null;
  }

  /**
   * Fetch the community list from paeapi and cache it. Concurrent callers share the same
   * in-flight request; subsequent calls return the cached list unless `force` is true.
   */
  async load(force = false): Promise<Community[]> {
    if (!force && this._communities$.value) return this._communities$.value;
    if (this._loadingPromise) return this._loadingPromise;

    this._loadingPromise = firstValueFrom(this.http.get<Community[]>(environment.paeApiUrl + "communities", this.helperService.getHttpOptions()))
      .then((list) => {
        this._all = Array.isArray(list) ? list : [];
        this._viewerIsEmployee = this._isEmployee();
        return this._publish(this._all);
      })
      .catch((err) => {
        // Fail closed — leave the cache empty. Callers that need a community for FSP will
        // still get a sensible default via `getCommunity(FSP_COMMUNITY_STRING_ID)` at the
        // call site if they wrap the result with the FSP fallback pattern.
        console.error("CommunitiesService.load failed", err);
        return this._communities$.value ?? [];
      })
      .finally(() => {
        this._loadingPromise = null;
      }) as Promise<Community[]>;

    return this._loadingPromise;
  }

  /**
   * The cache everything else reads — the list, `communitiesMap`, `communities$` — holds only
   * what this viewer may see: `employee_only` communities are left out for non-employees, so
   * the hub's rail, feeds, search and explorers (web and mobile) never offer them.
   */
  private _publish(all: Community[]): Community[] {
    const visible = all.filter((c) => !c?.employee_only || this._viewerIsEmployee);
    this.communitiesMap = {};
    for (const c of visible) {
      if (c?.string_id) this.communitiesMap[c.string_id] = c;
    }
    this._communities$.next(visible);
    return visible;
  }

  private _isEmployee(): boolean {
    const users = this._users();
    const user = users?.user;
    return !!user && !!users?.checkEmployeeAccess(user);
  }

  /**
   * Fetch every community — hidden ones included — for the admin management UI. Bypasses
   * the shared `communities$` cache so the public /communities landing keeps rendering
   * only the visible ones. Direct HTTP, no state, admin owns the returned list.
   */
  async loadAllForAdmin(): Promise<Community[]> {
    const list = await firstValueFrom(this.http.get<Community[]>(environment.paeApiUrl + "communities?includeHidden=1", this.helperService.getHttpOptions()));
    return Array.isArray(list) ? list : [];
  }

  /**
   * Get a community by string_id from the cache. Returns undefined if the cache hasn't been
   * loaded yet or the community doesn't exist. Callers who need FSP-fallback behavior should
   * do `getCommunity(id) ?? getCommunity(FSP_COMMUNITY_STRING_ID)`.
   */
  getCommunity(stringId: string): Community | undefined {
    return this.communitiesMap[stringId];
  }

  /** All cached communities. Empty array if the cache hasn't been loaded yet. */
  listCommunities(): Community[] {
    return this._communities$.value ?? [];
  }

  /** Force a re-fetch from paeapi. Useful after an admin edit in web-admin. */
  refresh(): Promise<Community[]> {
    return this.load(true);
  }

  /**
   * Convenience: does this community exist in the cache? Used by route guards that need
   * to decide whether `:communityId` is a known community.
   */
  isKnownCommunity(stringId: string): boolean {
    return stringId === FSP_COMMUNITY_STRING_ID || !!this.communitiesMap[stringId];
  }

  /**
   * Does `user` have community-admin authority on `communityStringId`? True when the user
   * is a member of any group listed in that community's `admin_roles`. Falls back to a
   * global-admin check (`user.groups` contains "admin") so global admins retain access
   * without being added to each community.
   *
   * The frontend uses this to gate UI (hide/show the manager view); paeapi enforces the
   * same check server-side, so a bypass at this layer just produces a 418 from the API.
   * DENG-728 Phase 4.
   */
  isCommunityAdmin(user: any, communityStringId: string): boolean {
    if (!user || !communityStringId) return false;
    const groupNames: string[] = Array.isArray(user.groups) ? user.groups.map((g: any) => (typeof g === "string" ? g : g?.name)).filter(Boolean) : [];
    if (groupNames.includes("admin")) return true;
    const community = this.getCommunity(communityStringId);
    if (!community) return false;
    const adminRoles = community.admin_roles || [];
    return adminRoles.some((role) => groupNames.includes(role));
  }

  /**
   * Whether `user` may start a post in `channel` of `communityStringId` — the channel's
   * `posting_groups` restriction, with community moderators/admins always allowed. Replies
   * aren't restricted; don't call this for them.
   */
  canPostInChannel(user: any, communityStringId: string, channel: Pick<CommunityChannel, "posting_groups"> | null | undefined): boolean {
    if (!channel?.posting_groups?.length) return true;
    return canPostInChannel(channel, userGroupNames(user), this.isCommunityModerator(user, communityStringId));
  }

  /** Same as isCommunityAdmin but for `moderator_roles`. */
  isCommunityModerator(user: any, communityStringId: string): boolean {
    if (this.isCommunityAdmin(user, communityStringId)) return true;
    if (!user || !communityStringId) return false;
    const groupNames: string[] = Array.isArray(user.groups) ? user.groups.map((g: any) => (typeof g === "string" ? g : g?.name)).filter(Boolean) : [];
    const community = this.getCommunity(communityStringId);
    if (!community) return false;
    const moderatorRoles = community.moderator_roles || [];
    return moderatorRoles.some((role) => groupNames.includes(role));
  }
}
